import { BaseControl } from '@Common/Controls/BaseControl';
import { selectMode } from '@Common/Controls/BaseMenu';
import { List } from '@Common/Controls/List';
import { Popup } from '@Common/Controls/Popup';
import constants from '@Common/Core/Constant';
import { safeDestroys } from '@Common/Core/Destroyable';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { IGridDropDownEditorProperties } from './SimpleGridLight';

export class InputDropDown extends BaseControl {
    protected readonly eventBag = new MulticastDelegateBag();
    public onShowContent = this.eventBag.create<IHandler>();
    public onRemoveContent = this.eventBag.create<IHandler>();
    public onResizeStart = this.eventBag.create<IHandler>();
    public onResizeStop = this.eventBag.create<IHandler>();
    public onResize = this.eventBag.create<IHandler>();

    public onChange = this.eventBag.create<IAnyCallback>();
    public onKeyUp = this.eventBag.create<IAnyCallback>();

    private _currentCompareTerms: string;
    private _isContentVisible: any;
    private _lockSelected: boolean;
    private _list: List;
    private _titleControl: JQuery<HTMLElement>;
    private _content: JQuery<HTMLElement>;
    private _popup: Popup;

    public destroy(): void {
        safeDestroys(this.eventBag, this._list, this._titleControl, this._content, this._popup);
    }

    constructor(
        param: IGridDropDownEditorProperties & {
            setButtonText?: string;
            hideEmpty?: boolean;
            lazyLoad?: boolean;
            skipKeyHandling?: boolean;
            onAppendData?: () => void;
        }
    ) {
        super(
            $.extend(
                {
                    css: null,
                    className: '',
                    width: '100%',
                    height: null,
                    maxContentHeight: 350,

                    caption: '',

                    checkOnClick: false,

                    resizeble: false,
                    checkable: false,
                    sortable: true,

                    searchPanel: false,
                    showClearAllButton: false,
                    showSetButton: false,
                    setButtonText: undefined,

                    onSearch: $.noop,
                    onShowContent: $.noop,
                    onAppendData: $.noop,

                    hideEmpty: true,

                    lazyLoad: false,
                    skipKeyHandling: false
                },
                param
            )
        );

        this._currentCompareTerms;
        this._isContentVisible;
        this._init();
        this._lockSelected = false;
    }

    public getList(): List {
        return this._list;
    }

    public closePopup(): void {
        this.removeContent();
        this._currentCompareTerms = '';
        this._list.setNodesVisibility();
    }

    protected _init(): void {
        // init root
        this._root = $('<div class="drop-down input-drop-down"/>')
            .on(pointerHelper.pointerDownEventName, () => {
                this._isContentVisible = this._content.is(':visible');
            })
            .on('click', e => {
                e.stopPropagation();

                if (this.enabled()) {
                    if (!this._isContentVisible) {
                        this.showContent();
                    } else {
                        this.removeContent();
                    }
                }
            })
            .addClass(this._param.selectMode === selectMode.simple ? 'single' : 'multiple')
            .addClass(this._param.titleStyle)
            .toggleClass('component-drop-down', this._param.isComponent === true)
            .css({ lineHeight: this._param.height + 'px' });

        // input for drop down
        this._titleControl = $('<input type="text"/>')
            .appendTo(this._root)
            .on('click', e => e.stopPropagation())
            .on('keydown', e => {
                if (e.which == constants.keyCodes.ENTER && this._root.hasClass('clicked')) {
                    e.stopPropagation();
                }
            })
            .on('keyup', e => {
                if (this._param.skipKeyHandling) {
                    return;
                }

                if (e.which == constants.keyCodes.ENTER && this._root.hasClass('clicked')) {
                    const nodes = this._list.getVisibleNodes();
                    if (nodes.length) {
                        this._titleControl.val(nodes[0].data.name);
                    }

                    this.closePopup();
                    return;
                }

                this._lockSelected = true;
                this._list.selectedItems([]);
                this.setCompareItems(this._titleControl.val() as string);
                this._lockSelected = false;
            })
            .on('blur', () => {
                if (!this._param.checkOnClick) {
                    this.onChange(this._titleControl.val());
                }
            });

        $('<span class="dropdown-arrow"/>').appendTo(this._root);

        this._param.margins && this._root.applyMargins(this._param.margins);
        this._applyDefaultParam();

        this._content = $('<div class="drop-down-content popup-shadow"/>')
            .css({
                position: 'absolute',
                maxHeight: this._param.maxContentHeight
            })
            .on('removePopup', () => {
                this.removeContent();
            });

        this._list = new List({
            height: '100%',
            width: '100%',
            checkable: this._param.checkable,
            sortable: this._param.sortable,
            comparer: this._param.comparer,
            lazyLoad: this._param.lazyLoad,
            visibilityComparer: (data: { name: string }) => {
                if (this._param.visibilityComparer && !this._param.visibilityComparer(data)) {
                    return false;
                }
                if (!this._currentCompareTerms) {
                    return true;
                } else {
                    return data.name.toLowerCase().indexOf(this._currentCompareTerms.toLowerCase()) != -1;
                }
            },
            onAppendData: this._param.onAppendData,
            checkOnClick: this._param.checkOnClick,
            actions: {
                searchPanel: this._param.searchPanel,
                clearButton: this._param.showClearAllButton,
                setButton: this._param.showSetButton,
                setButtonText: this._param.setButtonText,
                onSet: () => {
                    this.onChange(this._list.checkedItems());
                    this.removeContent();
                },
                onSearch: this._param.onSearch
            }
        });

        if (!this._param.checkOnClick) {
            this._list.onItemClick((data: { name: any }) => {
                this.onChange(data.name);
                this.setTitleDescription(data.name);
                this.removeContent();
            });

            this._list.onSelectionChanged(() => {
                if (!this._lockSelected) {
                    this.setTitleDescription(this._list.getSelectedItemsDescription());
                    this.removeContent();

                    this.onChange(this._list.getSelectedItemsDescription());
                }
            });
        }

        this._list.getControl().appendTo(this._content);

        // init popup
        this._popup = new Popup({
            padding: 0,
            content: this._content,
            isHidden: true,
            jointHeight: this._param.height
        });

        this._popup.onClose(() => {
            this.onRemoveContent();
        });

        if (this._param.resizable) {
            this._popup.getControl().resizable({
                minWidth: 200,
                minHeight: 50,
                stop: () => {
                    this._adjustContent();
                    this.onResizeStop();
                },
                resize: () => {
                    this._adjustContent();
                    this.onResize();
                },
                start: () => {
                    this.onResizeStart();
                }
            });
        }
    }

    public setCompareItems(currentValue: string): void {
        if (currentValue !== '') {
            this.onKeyUp(currentValue);
            this._currentCompareTerms = currentValue;
            this._list.setNodesVisibility();

            if (this._list.getVisibleNodes().length || !this._param.hideEmpty) {
                this.showContent();
            } else {
                this.closePopup();
            }
        } else {
            this._currentCompareTerms = currentValue;
            this._list.setNodesVisibility();
            this.showContent();
        }
    }

    public adjust(width: number, height: number): any {
        if (this._popup) {
            this._popup.setParams({
                jointHeight: height
            });
        }

        return super.adjust(width, height);
    }

    private _adjustContent(): void {
        const item = this._list.selectedItems()[0];
        if (item) {
            this._list.scrollToItem(item);
        }
        this._list.adjust(null, this.getHeight());
    }

    public getHeight(height?: number): number {
        return Math.min(
            this._param.maxContentHeight,
            height ? height : this._list.getActionsHeight() + this._list.getScrollWrapper().children().outerHeight()
        );
    }

    public removeContent(): void {
        this._popup.hide();
        this._root.removeClass('clicked');
        this.onRemoveContent();
    }

    public showContent(): void {
        this._root.addClass('clicked');
        this._popup.show(this._root.offset().left, this._root.offset().top);
        this._list._actions?.buttons[0].visible(Boolean(this._list.getVisibleNodes().length));
        this._param.onShowContent();
        this.onShowContent();
        this._adjustContent();
        this._popup.show(
            null,
            null,
            Math.max(this._root.outerWidth(), this._list.getControl().outerWidth()),
            Math.min(this._param.maxContentHeight, this.getHeight())
        );
    }

    public setTitleDescription(titleDescription: any): void {
        this._titleControl.val(titleDescription || '');
    }

    public setTitleStyle(css: any): void {
        this._root.css(css);
    }

    public getInput(): JQuery<HTMLElement> {
        return this._titleControl;
    }

    public getValue(): any {
        const listValue = this._param.checkable ? this._list.checkedItems() : this._list.selectedItems();
        if (listValue && listValue.length) {
            return listValue;
        }

        const inputValue = this._titleControl.val();
        return [
            {
                id: inputValue,
                name: inputValue
            }
        ];
    }
}
